import React, { useState } from 'react';
import { Building2, Palette, Globe, Users, Settings, ChevronRight, Mail } from 'lucide-react';
import { useWorkspaces } from './WorkspaceProvider';
import { workspaceSubdomainHost } from '../../lib/platformDomain';
import WorkspaceSettings from './WorkspaceSettings';
import type { UserProfile } from '../../types';

/**
 * Workspace settings section — shows the active workspace's identity + branding
 * and opens the full Workspace Settings console (brand config, domains, team,
 * custom roles, email sender). Visible to internal workspace admins only —
 * never to external org accounts (referrer / employer / sponsor), whose
 * workspaceMembers 'admin' stamp denotes org ownership, not adminship.
 * Embed it in dashboard Settings/Profile tabs.
 */
export default function WorkspaceSettingsSection({ compact = false, profile }: { compact?: boolean; profile?: UserProfile | null }) {
  const { activeWorkspace, platformAdmin } = useWorkspaces();
  const [open, setOpen] = useState(false);

  // Match the server's workspace-admin tiers.  A workspace Super Admin or
  // Master must not lose the Settings landing section while the API correctly
  // grants settings:edit.
  const EXTERNAL_ROLES = ['employer', 'referrer', 'enhanced_referrer', 'sponsor', 'applicant', 'agent'];
  const activeRole = String(activeWorkspace?.role || '').toLowerCase();
  // The stamp-based EXTERNAL_ROLES check above can never catch an org owner:
  // their membership role IS 'admin' (the org-ownership stamp). Discriminate
  // on the PROFILE like the server does (isOrgConsoleProfile: primaryRole
  // first, then role) and deny when either source names an org role.
  // TEAMS & ROLES: this gate interprets the org-ownership stamp — org accounts are not workspace team members — see the canonical note above isOrgConsoleProfile in serverWorkspaces.ts.
  const profileRole = String(profile?.role || '').toLowerCase();
  const orgProfileRole = String(profile?.primaryRole || profile?.role || '').toLowerCase();
  const isExternalOrgAccount =
    ['referrer', 'employer', 'sponsor', 'enhanced_referrer'].includes(orgProfileRole) ||
    ['referrer', 'employer', 'sponsor'].includes(profileRole);
  // Same one-rule-everywhere condition as the profile dropdown: when a
  // profile is supplied, the session must be an admin session (a dual-role
  // account in a non-admin shell keeps its workspaceRole but must not
  // surface workspace settings there).
  const isAdminSessionRole = !profile || ['admin', 'platform_admin', 'owner', 'super', 'master'].includes(profileRole);
  const isAdmin = (platformAdmin || ['admin', 'owner', 'super', 'master'].includes(activeRole)) && !EXTERNAL_ROLES.includes(activeRole) && !isExternalOrgAccount && isAdminSessionRole;
  if (!activeWorkspace || !isAdmin) return null;

  const branding = activeWorkspace.branding || {};
  const customDomainCount = (activeWorkspace as any).customDomains?.length || 0;

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Header — a dedicated space for the workspace settings section,
            mirroring the Settings tab header pattern. */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-slate-200 bg-white shrink-0 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="h-9 w-9 rounded-xl btn-primary flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Workspace Settings</h1>
              <p className="text-xs text-slate-500">
                {activeWorkspace.orgName || activeWorkspace.name} · {workspaceSubdomainHost(activeWorkspace.slug)}
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl btn-primary text-white text-xs font-bold cursor-pointer"
          >
            <Settings className="h-3.5 w-3.5" /> Edit Workspace
          </button>
        </div>

        <div className="px-4 md:px-6 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {branding.logoUrl ? <img src={branding.logoUrl} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-slate-300" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{branding.businessName || activeWorkspace.orgName || activeWorkspace.name}</p>
              <p className="text-[10px] text-slate-400">{activeWorkspace.plan} plan · {activeWorkspace.status}</p>
            </div>
            <span className={`ml-auto text-[11px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${activeWorkspace.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
              {activeWorkspace.status}
            </span>
          </div>

          {!compact && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <Palette className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-500">Branding</span>
                <span className="ml-auto flex gap-1">
                  <span className="h-3 w-3 rounded-full" style={{ background: branding.primary || '#2563eb' }} />
                  <span className="h-3 w-3 rounded-full" style={{ background: branding.accent || '#059669' }} />
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <Globe className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-500">Domains</span>
                <span className="ml-auto text-[10px] font-bold text-slate-600">{customDomainCount ? `+${customDomainCount}` : 'Default'}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <Users className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-500">Workspace Team</span>
                <span className="ml-auto text-[10px] font-bold text-slate-600 capitalize">{activeWorkspace.role || 'member'}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <Mail className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-500">Sender</span>
                <span className="ml-auto text-[10px] font-bold text-slate-600">{activeWorkspace.emailSender?.email || 'noreply@edutrouve.org'}</span>
              </div>
            </div>
          )}

          <button onClick={() => setOpen(true)} className="text-xs font-bold text-brand hover:underline inline-flex items-center gap-0.5 cursor-pointer">
            Open Workspace Settings <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      <WorkspaceSettings open={open} onClose={() => setOpen(false)} />
    </>
  );
}
